<template>
  <div>
    <loading-container v-if="loading"></loading-container>

    <div v-else class="container">
      <div class="row">
        <div class="col-xs-6">
          <help-text>
            <h3>
              Timeline
            </h3>

            <p>
              This page displays the history of the subject. The events are sorted from newest to oldest.
            </p>

            <p>
              You may use the controls for filtering the data.
            </p>

            <div class="btn-group">
              <button type="button" class="btn btn-black btn-sm dropdown-toggle" data-toggle="dropdown">
                <i class="fa fa-plus"></i> Add <span class="caret"></span>
              </button>

              <ul class="dropdown-menu">
                <li>
                  <a href="#create-comment-modal" data-toggle="modal">
                    <i class="fa fa-comment"></i> Create Comment
                  </a>
                </li>

                <li>
                  <a href="#create-reminder-modal" data-toggle="modal">
                    <i class="fa fa-bell"></i> Create Reminder
                  </a>
                </li>
              </ul>
            </div>
          </help-text>
        </div>

        <div class="col-xs-18">
          <form @submit.prevent="navigate(1)" style="margin-bottom: 10px;">
            <div class="row">
              <div class="col-xs-8">
                <select-widget
                    v-model="filters.user"
                    :options="userOptions"
                    :nullable="true"
                    placeholder="(all users)"
                    class="input-sm"
                ></select-widget>
              </div>

              <div class="col-xs-8">
                <select-widget
                    v-model="filters.event"
                    :options="this.eventOptions"
                    :nullable="true"
                    placeholder="(all events)"
                    class="input-sm"
                ></select-widget>
              </div>

              <div class="col-xs-8">
                <button type="submit" class="btn btn-default btn-block btn-black btn-sm">
                  <i class="fa fa-filter"></i> Filter
                </button>
              </div>
            </div>
          </form>

          <hr class="panel-separator" style="margin-bottom: 10px">

          <log-line
              v-for="logLine in logLines.data"
              :log-line="logLine"
              :key="logLine.id"
              @edit="editLogLine(logLine)"
              @resolve="resolveLogLine(logLine)"
              @postpone="postponeLogLine(logLine)"
              @toggle-sticky="toggleSticky(logLine)"
          ></log-line>

          <div v-if="!logLines.data.length" class="text-center" style="margin: 40px 0">
            <i>
              There are no log lines to display.
            </i>
          </div>

          <pager :pagination="logLines.meta" @navigate="navigate"></pager>
        </div>
      </div>

      <CreateCommentModal :subject="owner" @created="navigate(1)"/>

      <CreateReminderModal :subject="owner" @created="navigate(1)"/>

      <PostponeReminderModal :reminder="editing" @postponed="load"/>

      <UpdateReminderModal :reminder="editing" @updated="load"/>
    </div>
  </div>
</template>

<script>
import http from '@/http'
import modal from '@/modal'
import notify from '@/notify'
import CreateCommentModal from '@/pages/Shared/Modals/CreateComment'
import CreateReminderModal from '@/pages/Shared/Modals/CreateReminder'
import HelpText from '@/components/HelpText'
import Load from "@/mixins/Load";
import LoadingContainer from "@/components/Layout/LoadingContainer";
import LogLine from "@/components/LogLine";
import Pager from "@/components/Lists/Pager";
import PostponeReminderModal from '@/components/Modals/Reminders/PostponeReminderModal'
import SelectWidget from '@/vendor/forms/FormGroups/Widgets/Select'
import UpdateReminderModal from '@/pages/Shared/Modals/UpdateReminder'

export default {
  components: {
    CreateCommentModal,
    CreateReminderModal,
    HelpText,
    LoadingContainer,
    LogLine,
    Pager,
    PostponeReminderModal,
    SelectWidget,
    UpdateReminderModal
  },

  mixins: [Load],

  model: {
    prop: "owner",
    event: "owner-updated"
  },

  props: ["owner"],

  data() {
    return {
      logLines: null,
      users: null,
      editing: null,
      filters: {
        user: this.getQueryParam('user'),
        event: this.getQueryParam('event'),
        page: this.getQueryParam('page', 1),
      }
    };
  },

  watch: {
    '$route'() {
      this.load()
    }
  },

  mounted() {
    this.load()
  },

  computed: {
    queryString() {
      return '?' + _(this.filters).pickBy().map((value, key) => key + '=' + value).join('&')
    },

    eventOptions() {
      return [
        {value: 'company-created', label: 'Company Created'},
        {value: 'company-updated', label: 'Company Updated'},
        {value: 'person-created', label: 'Person Created'},
        {value: 'person-updated', label: 'Person Updated'},
        {value: 'invoice-created', label: 'Invoice Created'},
        {value: 'invoice-drafted', label: 'Invoice Drafted'},
        {value: 'invoice-paid', label: 'Invoice Paid'},
        {value: 'subscription-created', label: 'Subscription Created'},
        {value: 'subscription-updated', label: 'Subscription Updated'},
        {value: 'order-created', label: 'Order Created'},
        {value: 'order-updated', label: 'Order Updated'},
        {value: 'order-confirmation-created', label: 'Order Confirmation Created'},
        {value: 'reminder-created', label: 'Reminder'},
        {value: 'comment-created', label: 'Comment'},
        {value: 'opportunity-created', label: 'Opportunity Created'},
        {value: 'opportunity-updated', label: 'Opportunity Updated'},
        {value: 'opportunity-closed', label: 'Opportunity Closed'},
        {value: 'opportunity-converted', label: 'Opportunity Converted'},
      ]
    },

    userOptions() {
      return this.users.data.map(user => {
        return {
          value: user.id,
          label: user.attributes.full_name
        }
      })
    }
  },

  methods: {
    load() {
      this.loading = true

      Promise.all([http.get('/api/v1/log-lines' + this.queryString, {
        'owner[id]': this.owner.data.id,
        'owner[type]': this.owner.data.type
      }), http.get('/api/v2/users')])
          .then(responses => {
            this.logLines = responses[0].data

            this.users = responses[1].data

            this.loading = false
          })
    },

    navigate(page) {
      this.filters.page = page

      if ('/' + this.owner.data.type + '/' + this.owner.data.id + '/timeline' + this.queryString === this.$route.fullPath) {
        this.load()
      }

      this.$router.push('timeline' + this.queryString)
    },

    getQueryParam(key, defaultValue = null) {
      return typeof this.$route.query[key] !== 'undefined' ? this.$route.query[key] : defaultValue
    },

    editLogLine(logLine) {
      this.editing = {id: logLine.subject.id, attributes: logLine.subject}

      $('#update-reminder-modal').modal('show')
    },

    resolveLogLine(logLine) {
      modal.confirm('Are you sure that you want to resolve the reminder?', ()=> {
        http.post('/api/v2/reminders/' + logLine.subject.id + '/resolve')
            .then(() => {
              notify.success('The reminder was resolved')

              this.load()
            })
      })
    },

    postponeLogLine(logLine) {
      this.editing = {id: logLine.subject.id, attributes: logLine.subject}

      $('#postpone-reminder-modal').modal('show')
    },

    toggleSticky(logLine) {
      http.put('/api/v1/log-lines/' + logLine.id, {is_sticky: !logLine.is_sticky})
          .then(() => notify.success('The log line was made sticky'))
          .then(this.load)
    }
  }
};
</script>
